import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

const skills = [
  'JavaScript',
  'TypeScript',
  'React',
  'D3.js',
  'UI Performance',
  'Data Visualization',
  'REST APIs',
  'Event-Driven Systems',
  'PostgreSQL',
  'Linux',
  'Docker',
  'AWS',
];



  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
  Hello, I’m Sachin. I’m a Software Engineer with over four years of experience building
  production-grade frontend and full-stack systems using React, TypeScript, and modern
  web architectures. I focus on designing reliable, high-performance user interfaces
  that operate under real-world conditions and integrate tightly with backend services
  and distributed systems.
</p>

<p>
  I previously worked at <a href="https://www.tcs.com/">Tata Consultancy Services</a> as a Software Engineer,
  where I built scalable React dashboards and end-to-end data workflows integrated with
  REST APIs and asynchronous backend pipelines. My work emphasized handling large operational
  datasets, designing robust UI states, and ensuring reliability across long-running
  workflows in production environments.
</p>

<p>
  I also served as a Graduate Teaching Assistant at
  <a href="https://www.stevens.edu/">Stevens Institute of Technology</a>, mentoring graduate students
  in full-stack development and backend systems. During this time, I built internal automation
  tools using Node.js, Puppeteer, and MongoDB that reduced manual grading effort from over
  100 hours per week to minutes, demonstrating scalable workflow automation.
</p>

<p>
  Currently, I’m a Frontend Software Engineer at <a href="https://www.lpl.com/">LPL Financial</a>, where I design and build React and TypeScript
  interfaces for high-throughput financial systems. I work on operational UIs that support
  real-time decision-making for asynchronous transaction workflows, integrating with
  Spring Boot APIs, PostgreSQL-backed data stores, and Kafka-driven event streams.
</p>

<p>
  My work involves building data-dense tables, drill-down views, and visualizations to
  support time-sensitive human decisions, optimizing UI performance for scale, and
  ensuring correctness under edge cases and production load. I care deeply about building
  systems that are observable, reliable, and safe to operate in regulated environments.
</p>



<p>Here are a few technologies I’ve been working with recently:</p>

          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
